import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { assignOffice } from "../../../core/data/redux/employeesSlice";
import { getOfficeLocation } from "../../../core/data/redux/officeLocationSlice";
import { getReportingEmployees } from "../../../core/data/redux/reportingEmployeesSlice";
import { useDispatch, useSelector } from "react-redux";

interface TeamOfficesProps {
  employee: any;
}

const TeamOffices = (props: TeamOfficesProps) => {
  const { employee } = props;
  const tableRef = useRef<{ resetSelection: ([]) => void }>(null);
  const dispatch = useDispatch();
  const officeLocationState = useSelector(
    (state: any) => state.officeLocationState,
  );
  const employeesState = useSelector((state: any) => state.employeesState);
  const [dataList, setDataList] = React.useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });
  const [selectedRows, setSelectedRows] = useState<any[] | null>(null);

  const handleReset = (data: any[]) => {
    if (tableRef.current) {
      tableRef.current.resetSelection(data);
    }
  };

  React.useEffect(() => {
    if (employee) {
      dispatch(
        getOfficeLocation({
          page: 1,
          limit: 100,
          orderColumn: "",
          orderBy: "",
          query: "",
          filters: "",
          is_verified: "",
          date_from: "",
          is_Active: "",
          date_to: "",
          month: "",
          year: "",
        }),
      );
    }
  }, [employee]);

  const handleAssign = async () => {
    if (selectedRows && selectedRows.length > 0) {
      // employee.employee.id is the actual employee_id (e.g., 12246)
      // NOT the id from assigned_offices array (e.g., 390)
      const employeeId = employee?.employee?.id;
      console.log(
        "Sending employee_id:",
        employeeId,
        "for empId:",
        employee?.empId,
      );

      const payload = {
        employee_id: employeeId,
        office_location_ids: selectedRows,
      };
      await dispatch(assignOffice(payload));
    }
  };

  // Listen for successful assign office and refresh reporting employees list
  React.useEffect(() => {
    if (
      employeesState.loading === "fulfilled" &&
      employeesState.action === "assign-office"
    ) {
      // Refresh reporting employees list
      dispatch(
        getReportingEmployees({
          page: 1,
          limit: 10,
          query: "",
        }),
      );
    }
  }, [employeesState.loading, employeesState.action]);

  // Set pre-selected offices based on employee's existing office assignments
  React.useEffect(() => {
    // Check for assigned_offices in employee?.employee?.assigned_offices
    const assignedOffices = employee?.employee?.assigned_offices || [];
    const selectedOfficeIds = assignedOffices.map(
      (item: any) => item.office_location_id,
    );
    console.log("Pre-selecting offices:", selectedOfficeIds);
    handleReset(selectedOfficeIds);
    setSelectedRows(selectedOfficeIds);
  }, [employee]);

  React.useEffect(() => {
    if (
      officeLocationState.loading === "fulfilled" &&
      officeLocationState.action === "list"
    ) {
      // officeLocationState.data = { data: [...], total, page, limit }
      const result = officeLocationState?.data || null;
      if (result && result.data && result.data.length > 0) {
        let officeLocations = result.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          address: item.address,
          phone: item.phone || "",
          email: item.email || "",
          key: item.id,
          status: item.status,
        }));
        setDataList(officeLocations);
        setPagination({
          ...pagination,
          total: result.total || officeLocations.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [officeLocationState.loading, officeLocationState.action]);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 100,
      render: (text: string, record: any, index: number) => (
        <>
          <div>
            {pagination.current
              ? index +
                pagination.current * pagination.pageSize -
                pagination.pageSize +
                1
              : index + 1}
          </div>
        </>
      ),
      sorter: (a: any, b: any) => a.id.length - b.id.length,
    },

    {
      title: "Name",
      dataIndex: "name",
      width: 200,
      onCell: () => ({
        style: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      }),
      sorter: (a: any, b: any) => a.name.length - b.name.length,
    },
    {
      title: "Address",
      dataIndex: "address",
      width: 200,
      onCell: () => ({
        style: {
          whiteSpace: "normal",
          wordWrap: "break-word",
        },
      }),
      sorter: (a: any, b: any) => a.address.length - b.address.length,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a: any, b: any) => a.phone.length - b.phone.length,
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a: any, b: any) => a.email.length - b.email.length,
    },
  ];

  const handlePagination = (paginationData: any) => {
    setPagination({
      current: paginationData.page,
      pageSize: paginationData.pageSize,
      total: paginationData.total || 10,
    });
    dispatch(
      getOfficeLocation({
        page: paginationData.page,
        limit: paginationData.pageSize,
        orderColumn: "",
        orderBy: "",
        query: "",
        filters: "",
        is_verified: "",
        date_from: "",
        is_Active: "",
        date_to: "",
        month: "",
        year: "",
      }),
    );
  };

  const handleOnRowSelection = (selectedRowKeys: any) => {
    setSelectedRows(selectedRowKeys);
  };

  return (
    <div>
      <>
        <div className="pt-2">
          <Table
            columns={columns}
            dataSource={dataList}
            Selection={true}
            paginationConfig={pagination}
            handlePagination={handlePagination}
            loading={officeLocationState.loading === "pending"}
            onRowSelection={handleOnRowSelection}
            ref={tableRef}
            scroll={{ y: 300 }}
          />
        </div>
        <div className="row align-items-center">
          <div className="col-md-12">
            <div className="datatable-paginate mt-4" />
            <div className="d-flex justify-content-center pb-3 pt-0">
              {selectedRows && selectedRows.length > 0 ? (
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                  onClick={() => handleAssign()}
                >
                  Assign
                </Link>
              ) : (
                <Link
                  to="#"
                  data-bs-dismiss="modal"
                  className="btn btn-primary"
                >
                  Select to assign
                </Link>
              )}
            </div>
          </div>
        </div>
      </>
    </div>
  );
};

export default TeamOffices;
